import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAuthStore } from '@/store/auth';
import { useProjectsStore } from '@/store/projects';
import { useNav } from '@/lib/nav';
import routes from '@/lib/routes';
import Header from '@/components/layout/header';
import { Plus, Folder, Database, Images, Clock, MoreHorizontal, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { scopedKey } from '@/lib/queryClient';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/auth/AuthProvider';

export default function Dashboard() {
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const { entitlement, isLoading } = useAuthStore();
  const { user } = useAuth();
  const { projects, loadProjects, createProject, setCurrentProject } = useProjectsStore();
  const { go } = useNav();
  const { toast } = useToast();
  const [upgradeTipOpen, setUpgradeTipOpen] = useState(false);

  // Load projects for the grid when user is loaded
  useEffect(() => {
    if (user && !isLoading) {
      loadProjects();
    }
  }, [user, isLoading, loadProjects]);

  // Show loading state while checking auth or loading projects
  if (isLoading) {
    return <div>Loading...</div>;
  }

  // Redirect if not logged in (only after loading is complete)
  useEffect(() => {
    if (!isLoading && !user) {
      go(routes.home());
    }
  }, [isLoading, user, go]);
  if (!user) return null;

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectName.trim()) return;

    try {
      setIsCreating(true);
      const project = await createProject(projectName.trim());
      setCurrentProject(project);
      setShowCreateProject(false);
      setProjectName('');
      go(routes.plotEditor.open(project.id) + '?tab=chat');
      
      toast({
        title: "Project created",
        description: `${projectName} has been created successfully`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create project",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateClick = () => {
    const maxProjects = entitlement?.maxProjects ?? 1;
    const shouldUpgrade = projects.length >= maxProjects;
    if (shouldUpgrade) {
      setUpgradeTipOpen(true);
      setTimeout(() => setUpgradeTipOpen(false), 2500);
      return;
    }
    setShowCreateProject(true);
  };

  const handleOpenProject = (project: any) => {
    setCurrentProject(project);
    go(routes.project.root(project.id));
  };

  const canCreateProject = (entitlement?.maxProjects ?? 1) > projects.length;
  const disabledCreate = !canCreateProject;
  const maxProjects = entitlement?.maxProjects ?? 1;
  const tooltipText = disabledCreate
    ? (maxProjects <= 1
      ? 'Max 1 project; multiple projects coming soon.'
      : `You reached your plan limit (max ${maxProjects} projects). Upgrade to increase your limit.`)
    : 'Create a new project';

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-6 py-8" data-testid="dashboard">
        {/* Dashboard Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">My Projects</h1>
            <p className="text-muted-foreground mt-1">
              Manage your scientific visualizations
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button
              onClick={() => {
                if (projects.length > 0) {
                  // Open the first project in chat mode
                  setCurrentProject(projects[0]);
                  go(routes.plotEditor.open(projects[0].id) + '?tab=chat');
                } else {
                  // Create a project and go to chat
                  handleCreateClick();
                }
              }}
              data-testid="button-start-chat"
              variant="default"
              disabled={projects.length === 0}
              className="disabled:bg-muted disabled:text-muted-foreground disabled:hover:bg-muted"
            >
              <Plus className="mr-2 h-4 w-4" />
              Create New Plot
            </Button>
            
            <TooltipProvider>
              <Tooltip open={upgradeTipOpen} onOpenChange={setUpgradeTipOpen}>
                <TooltipTrigger asChild>
                  <div className="relative">
                    <Button
                      onClick={handleCreateClick}
                      data-testid="button-create-project"
                      variant="default"
                      className="disabled:bg-muted disabled:text-muted-foreground disabled:hover:bg-muted"
                      disabled={disabledCreate}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Create Project
                    </Button>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="max-w-[260px]">{tooltipText}</div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        {/* Projects Grid */}
        {projects.length === 0 ? (
          <div className="text-center py-16">
            <Folder className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-medium text-muted-foreground mb-2">
              No projects yet
            </h3>
            <p className="text-muted-foreground mb-6">
              Create your first project to start generating publication-ready figures
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <ProjectListItem
                key={project.id}
                project={project}
                onOpen={() => handleOpenProject(project)}
                onOpenGallery={() => go(routes.project.gallery(project.id))}
              />
            ))}
          </div>
        )}

        {/* Upgrade Notice removed; handled by tooltip on Create */}
      </div>

      {/* Create Project Modal */}
      <Dialog open={showCreateProject} onOpenChange={setShowCreateProject}>
        <DialogContent data-testid="create-project-modal">
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateProject} className="space-y-4">
            <div>
              <Label htmlFor="project-name">Project Name</Label>
              <Input
                id="project-name"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="e.g., Gene Expression Analysis"
                required
                data-testid="input-project-name"
              />
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateProject(false)}
                className="flex-1"
                data-testid="button-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isCreating || !projectName.trim()}
                className="flex-1"
                data-testid="button-create"
              >
                {isCreating ? "Creating..." : "Create Project"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProjectListItem({ project, onOpen, onOpenGallery }: { project: any; onOpen: () => void; onOpenGallery: () => void }) {
  const projectId = project?.id;
  const { go } = useNav();
  const { user } = useAuth();
  const { data: summary } = useQuery({
    queryKey: scopedKey([user?.id, '/api/projects', projectId, 'summary']),
    enabled: !!projectId,
  });

  const updatedAt = project?.updatedAt ? new Date(project.updatedAt as any) : null;
  const summaryUpdated = (summary as any)?.lastEdit ? new Date((summary as any).lastEdit) : null;
  const createdAt = project?.createdAt ? new Date(project.createdAt as any) : new Date();
  const lastUpdated = summaryUpdated ?? updatedAt ?? createdAt;
  const { updateProject, deleteProject } = useProjectsStore();
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(project?.name || '');
  const renameCommittedRef = useRef(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!renaming) {
      setNameDraft(project?.name || '');
    }
  }, [project?.name, renaming]);

  const beginRename = () => {
    renameCommittedRef.current = false;
    setNameDraft(project?.name || '');
    setRenaming(true);
  };

  const commitRename = async () => {
    if (renameCommittedRef.current) return;
    renameCommittedRef.current = true;
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      setNameDraft(project?.name || '');
      setRenaming(false);
      return;
    }
    if (trimmed !== (project?.name || '')) {
      try {
        await updateProject(project.id, { name: trimmed });
      } catch (err) {
        console.error('Failed to rename project', err);
      }
    }
    setRenaming(false);
  };

  const formatRelativeTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  // Ensure users always perceive progress by enforcing a minimum visible delay
  const withMinDelay = async <T,>(promise: Promise<T>, minMs: number): Promise<T> => {
    const [result] = await Promise.all([
      promise,
      new Promise<void>((resolve) => setTimeout(resolve, minMs)),
    ]);
    return result;
  };

  return (
    <Card className="hover:shadow-lg transition-shadow relative" aria-busy={isDeleting} data-testid={`project-card-${project.id}`}>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {renaming ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  commitRename();
                }}
              >
                <Input
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onBlur={commitRename}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setRenaming(false);
                      setNameDraft(project?.name || '');
                    }
                  }}
                  autoFocus
                  className="text-lg font-semibold"
                />
              </form>
            ) : (
              <CardTitle
                className="text-lg font-semibold cursor-text truncate"
                title={project.name}
                onClick={beginRename}
              >
                {project.name || 'Untitled Project'}
              </CardTitle>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={beginRename}>Rename</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onOpen}>View</DropdownMenuItem>
              <DropdownMenuItem onClick={onOpenGallery}>Open gallery</DropdownMenuItem>
              <DropdownMenuItem onClick={() => go(routes.project.root(project.id) + '#datasets')}>View datasets</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => setConfirmOpen(true)}
              >
                Delete project
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete "{project.name}"?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete this project and all of its data, including plots, versions, files, and artifacts. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={isDeleting}
                aria-busy={isDeleting}
                  onClick={async () => {
                    if (isDeleting) return;
                    setIsDeleting(true);
                  // Close the confirm dialog immediately so progress UI isn't hidden under menus
                  setConfirmOpen(false);
                  const pendingToast = toast({ title: 'Deleting project…', description: 'This may take a few seconds.' });
                    try {
                    await withMinDelay(deleteProject(project.id), 700);
                    pendingToast.update({ id: pendingToast.id, title: 'Project deleted', description: `${project.name} has been removed.` });
                    setTimeout(() => pendingToast.dismiss(), 1500);
                    } catch (err) {
                    pendingToast.update({ id: pendingToast.id, title: 'Delete failed', description: err instanceof Error ? err.message : 'Unknown error', variant: 'destructive' });
                    setTimeout(() => pendingToast.dismiss(), 3000);
                    } finally {
                      setIsDeleting(false);
                    }
                  }}
                >
                Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
        <CardDescription>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {formatRelativeTime(lastUpdated)}</span>
          </div>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="flex-1 flex items-center justify-center gap-2" onClick={onOpenGallery}>
            <Images className="h-3 w-3" />
            Gallery
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1 flex items-center justify-center gap-2"
            onClick={() => go(routes.project.root(project.id) + '#datasets')}
          >
            <Database className="h-3 w-3" />
            Datasets
          </Button>
        </div>
      </CardContent>
      {isDeleting && (
        <div className="absolute inset-0 z-10 rounded-md bg-background/70 backdrop-blur-sm flex items-center justify-center">
          <div className="flex items-center gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            Deleting…
          </div>
        </div>
      )}
    </Card>
  );
}
